import { useState, useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";
import type { FichePreset } from "@/contexts/fieldPermsEditor";

type VisibilityMap = Record<string, boolean>;

// Cache en mémoire partagé entre instances, même patron que useFieldPermissions.
const cache: Partial<Record<FichePreset, VisibilityMap>> = {};
let cacheVersion = 0;
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return cacheVersion;
}

export function invalidateBienFichePresetCache() {
  for (const key of Object.keys(cache)) delete cache[key as FichePreset];
  cacheVersion++;
  listeners.forEach((cb) => cb());
}

/**
 * Visibilité effective d'un préréglage de popup fiche bien. `preset=null`
 * (popup ouverte hors Apparts for Rent / Pipeline ELS, ou rôle hors famille
 * collaborateur) : aucun fetch, tout visible — le rôle réel reste seul juge.
 *
 * Absence de ligne (preset, item_key) en base = visible (spec §6.2).
 */
export function useBienFichePreset(preset: FichePreset | null) {
  const version = useSyncExternalStore(subscribe, getSnapshot);
  const [map, setMap] = useState<VisibilityMap>(preset ? cache[preset] ?? {} : {});
  const [isLoading, setIsLoading] = useState(!!preset && !cache[preset]);

  useEffect(() => {
    if (!preset) {
      setMap({});
      setIsLoading(false);
      return;
    }
    if (cache[preset]) {
      setMap(cache[preset]!);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    supabase
      .from("bien_fiche_presets")
      .select("item_key, visible")
      .eq("preset", preset)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[useBienFichePreset]", error.message);
          setIsLoading(false);
          return;
        }
        const m: VisibilityMap = {};
        for (const row of (data ?? []) as { item_key: string; visible: boolean }[]) {
          m[row.item_key] = row.visible;
        }
        cache[preset] = m;
        setMap(m);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preset, version]);

  function presetVisible(itemKey: string): boolean {
    if (!preset) return true;
    if (isLoading) return true; // évite un flash "tout caché" pendant le chargement
    return map[itemKey] ?? true; // absence de ligne = visible
  }

  return { presetVisible, isLoading };
}
